import React, { forwardRef, useRef, useState, useEffect, type ChangeEvent } from 'react';
import { InputWrapper } from '../shared/InputWrapper/InputWrapper';
import { useNormalizedInputProps } from '../shared/hooks/useNormalizedInputProps';
import { textInputProps } from '../shared/utils/textInputProps';
import type { BaseInputProps } from '../shared/types/input.types';
import { useModalInputLayer } from '../../modal/ModalContext';

export interface TextAreaProps extends Omit<BaseInputProps<HTMLTextAreaElement>, 'onChange'> {
  /**
   * Specify the cols attribute for the underlying textarea
   */
  cols?: number;
  
  /**
   * Specify the rows attribute for the underlying textarea
   */
  rows?: number;
  
  /**
   * Optionally provide an `onChange` handler that is called whenever textarea is updated
   */
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  
  /**
   * Whether to enable the resize handle
   */
  enableResize?: boolean;
  
  /**
   * Specify the field layer for background color.
   * Layer 1: default (white in light, dark gray in dark)
   * Layer 2: darker in light, lighter in dark
   * Layer 3: even lighter in dark
   */
  layer?: 1 | 2 | 3;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    className,
    inputClassName,
    cols = 50,
    disabled = false,
    enableCounter = false,
    enableResize = true,
    helperText,
    hideLabel,
    id,
    inline = false,
    invalid = false,
    invalidText,
    labelText,
    layer = 1, // Default layer 1
    maxCount,
    onChange = () => {},
    onClick = () => {},
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    readOnly,
    required,
    rows = 4,
    size = 'md',
    value,
    defaultValue,
    warn = false,
    warnText,
    ...rest
  },
  ref
) {
  const prefix = 'core';
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const mergedRef = ref || textAreaRef;
  
  // Get effective layer from modal context or user prop
  const effectiveLayer = useModalInputLayer(layer, 1);
  
  // Track text count for counter feature
  const getInitialTextCount = (): number => {
    const targetValue = defaultValue || value || textAreaRef.current?.value || '';
    return targetValue.toString().length;
  };
  
  const [textCount, setTextCount] = useState(getInitialTextCount());
  
  useEffect(() => {
    setTextCount(getInitialTextCount());
  }, [value, defaultValue, enableCounter]);
  
  const normalizedProps = useNormalizedInputProps({
    id,
    readOnly,
    disabled,
    invalid,
    invalidText,
    warn,
    warnText,
  });
  
  const textAreaClasses = [
    `${prefix}-text-area`,
    normalizedProps.invalid && `${prefix}-text-area--invalid`,
    normalizedProps.warn && `${prefix}-text-area--warning`,
    size && `${prefix}-text-area--${size}`,
    effectiveLayer && effectiveLayer !== 1 && `${prefix}-text-area--field-${effectiveLayer}`,
    !enableResize && `${prefix}-text-area--no-resize`,
    inputClassName,
  ].filter(Boolean).join(' ');
  
  const sharedTextAreaProps: any = {
    id,
    onChange: (evt: ChangeEvent<HTMLTextAreaElement>) => {
      if (!normalizedProps.disabled) {
        setTextCount(evt.target.value?.length || 0);
        onChange(evt);
      }
    },
    onClick: (evt: React.MouseEvent<HTMLTextAreaElement>) => {
      if (!normalizedProps.disabled) {
        onClick(evt);
      }
    },
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    ref: mergedRef,
    className: textAreaClasses,
    title: placeholder,
    disabled: normalizedProps.disabled,
    readOnly,
    required,
    value,
    defaultValue,
    cols,
    rows,
    'aria-describedby': helperText ? normalizedProps.helperId : undefined,
    'data-invalid': normalizedProps.invalid || undefined,
    ...rest,
  };
  
  // Add maxLength if counter is enabled
  if (enableCounter && maxCount) {
    sharedTextAreaProps.maxLength = maxCount;
  }
  
  const textArea = (
    <textarea
      {...textInputProps({
        sharedTextInputProps: sharedTextAreaProps,
        invalid: normalizedProps.invalid,
        invalidId: normalizedProps.invalidId,
        warn: normalizedProps.warn,
        warnId: normalizedProps.warnId,
      })}
    />
  );
  
  return (
    <InputWrapper
      className={className}
      disabled={normalizedProps.disabled}
      enableCounter={enableCounter}
      helperText={helperText}
      hideLabel={hideLabel}
      id={id}
      inline={inline}
      invalid={normalizedProps.invalid}
      invalidText={invalidText}
      labelText={labelText}
      maxCount={maxCount}
      readOnly={readOnly}
      required={required}
      size={size}
      textCount={textCount}
      warn={normalizedProps.warn}
      warnText={warnText}
    >
      {textArea}
    </InputWrapper>
  );
});

TextArea.displayName = 'TextArea';
